/**
 * Auth session flag (UI/app state only — NOT server data).
 *
 * `status`:
 *  - "checking"      — INITIAL state at startup: persisted tokens are being
 *                      hydrated and validated (`useSessionRestore`). We do not
 *                      yet know whether there is a session, so the router shows
 *                      a skeleton instead of redirecting to /login. Every data
 *                      hook gates on `=== "authenticated"`, so this state keeps
 *                      queries disabled — no request is fired and no data leaks
 *                      before the check resolves. Session restore ALWAYS leaves
 *                      this state.
 *  - "authenticated" — an access token is held; hooks read/write the API.
 *  - "anonymous"     — checked, and there is no session; the router redirects
 *                      to /login.
 *
 * The actual user record is server data fetched via `useCurrentUser`
 * (TanStack Query), per the "Zustand for UI state only" rule.
 */

import { create } from "zustand";

export type AuthStatus = "checking" | "anonymous" | "authenticated";

interface AuthState {
  status: AuthStatus;
  setAuthenticated: () => void;
  setAnonymous: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  // Startup begins in "checking": the app must not redirect to /login before the
  // persisted token has been hydrated and validated (that flashed the login page).
  status: "checking",
  setAuthenticated: () => {
    set({ status: "authenticated" });
  },
  setAnonymous: () => {
    set({ status: "anonymous" });
  },
}));
